import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { authApi } from "./auth";
import { store } from "../store";
import { logout, setCredentials } from "../features/authSlice";
import { toast } from "react-toastify";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://localhost:5000";

const getAuthToken = () => {
  return localStorage.getItem("refreshToken");
};

const baseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    if (!isRefreshing) {
      isRefreshing = true;
      const refresh = getAuthToken();

      if (refresh) {
        try {
          const refreshResult = await api.dispatch(
            authApi.endpoints.refreshToken.initiate({ refreshToken: refresh })
          );

          if (refreshResult.data.respuestaToken !== null) {
            const newToken = refreshResult.data.respuestaToken.accessToken;
            const newRefreshToken = refreshResult.data.respuestaToken.refreshToken;

            const userData = {
              respuestaToken: {
                accessToken: newToken,
                refreshToken: newRefreshToken,
              },
            };
            store.dispatch(setCredentials(userData));
            processQueue(null, newToken);

            result = await baseQuery(
              {
                ...args,
                headers: {
                  ...args.headers,
                  Authorization: `Bearer ${newToken}`,
                },
              },
              api,
              extraOptions
            );
          } else {
            processQueue("Token refresh failed", null);
            store.dispatch(logout());
            toast.error(
              "Lo sentimos, tu sesión ha expirado debido a inactividad. Por favor, vuelve a iniciar sesión para continuar.",
              {
                position: toast.POSITION.TOP_RIGHT,
              }
            );
          }
        } catch (error) {
          processQueue(error, null);
          store.dispatch(logout());
          toast.error(
            "Error al intentar refrescar el token. Por favor, vuelve a iniciar sesión.",
            {
              position: toast.POSITION.TOP_RIGHT,
            }
          );
        } finally {
          isRefreshing = false;
        }
      } else {
        store.dispatch(logout());
        toast.error(
          "No se encontró un token de refresco. Por favor, vuelve a iniciar sesión.",
          {
            position: toast.POSITION.TOP_RIGHT,
          }
        );
      }
    } else {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
      .then(token => {
        result = baseQuery(
          {
            ...args,
            headers: {
              ...args.headers,
              Authorization: `Bearer ${token}`,
            },
          },
          api,
          extraOptions
        );
        return result;
      })
      .catch(err => {
        return result;
      });
    }
  }

  return result;
};

export const mensajeContactoApi = createApi({
  reducerPath: "mensajeContactoApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["MensajeContacto"],
  endpoints: (builder) => ({
    getListaMensajesContactos: builder.query({
      query: () => "/buscar-lista-mensaje-contacto",
      providesTags: ["MensajeContacto"],
    }),
    agregarMensajeContacto: builder.mutation({
      query: (nuevoContacto) => {
        const formData = new FormData();
        Object.keys(nuevoContacto).forEach((key) => {
          formData.append(String(key), nuevoContacto[key]);
        });
        return {
          url: `/agregar-mensaje-contacto`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["MensajeContacto"],
    }),
    modificarMensajeContacto: builder.mutation({
      query: (nuevoContacto) => {
        const formData = new FormData();
        Object.keys(nuevoContacto).forEach((key) => {
          formData.append(String(key), nuevoContacto[key]);
        });
        return {
          url: `/modificar-mensaje-contacto`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["MensajeContacto"],
    }),
    borrarMensajeContacto: builder.mutation({
      query: (id) => ({
        url: `/borrar-mensaje-contacto?Id=${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MensajeContacto"],
    }),
    getMensajeContactoById: builder.query({
      query: (id) => `buscar-mensaje-contacto-individual?id=${id}`,
    }),
    onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
      await queryFulfilled;
      dispatch(mensajeContactoApi.endpoints.getListaContactos.query());
    },
  }),
});

export const {
  useGetMensajeContactoByIdQuery,
  useGetListaMensajeContactosQuery,
  useAgregarMensajeContactoMutation,
  useModificarMensajeContactoMutation,
  useBorrarMensajeContactoMutation,
} = mensajeContactoApi;
